import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneIcon, MailIcon, MapPinIcon, ClockIcon, CheckCircleIcon, SendIcon, StarIcon, ShieldCheckIcon, ExternalLinkIcon } from 'lucide-react';
import { WhatsAppIcon } from '../components/WhatsAppIcon';
import { PageHero } from '../components/PageHero';
import { Button } from '../components/Button';
import { FadeIn } from '../components/FadeIn';
import { StatBadge } from '../components/StatBadge';
interface ContactPageProps {
  onNavigate?: (page: string) => void;
}
export function ContactPage({
  onNavigate
}: ContactPageProps) {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    privacyConsent: false
  });
  const [callbackForm, setCallbackForm] = useState({
    name: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isCallbackSubmitting, setIsCallbackSubmitting] = useState(false);
  const [isCallbackSuccess, setIsCallbackSuccess] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formState.privacyConsent) return;
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setFormState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        privacyConsent: false
      });
      setTimeout(() => setIsSuccess(false), 15000);
    }, 1500);
  };
  const handleCallbackSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!callbackForm.name || !callbackForm.phone) return;
    setIsCallbackSubmitting(true);
    setTimeout(() => {
      setIsCallbackSubmitting(false);
      setIsCallbackSuccess(true);
      setCallbackForm({
        name: '',
        phone: ''
      });
      setTimeout(() => setIsCallbackSuccess(false), 10000);
    }, 1000);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleCallbackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setCallbackForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  return <div className="w-full overflow-hidden" data-id="element-221">
      {/* Hero */}
      <PageHero title="Laten we kennismaken" subtitle="Een goed gesprek begint met een kop koffie. Bel, app of mail — ik reageer altijd snel en persoonlijk." label="Contact" data-id="element-222" />

      {/* Quick Contact Cards */}
      <section className="py-12 md:py-16 bg-white -mt-8 relative z-10" data-id="element-223">
        <div className="max-w-5xl mx-auto px-4 md:px-8" data-id="element-224">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-225">
            <FadeIn delay={0.1} data-id="element-226">
              <motion.a href="tel:0232052161" className="block bg-white p-6 rounded-2xl shadow-card border border-gray-100 text-center group" whileHover={{
              y: -4,
              boxShadow: '0 20px 40px rgba(0, 45, 90, 0.08)'
            }} transition={{
              duration: 0.2
            }} data-id="element-227">
                <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gold/10 transition-colors" data-id="element-228">
                  <PhoneIcon className="w-7 h-7 text-gold" data-id="element-229" />
                </div>
                <h3 className="text-lg font-bold text-navy mb-1" data-id="element-230">Bel mij</h3>
                <p className="text-xl font-bold text-gold mb-2" data-id="element-231">023-205 2161</p>
                <p className="text-sm text-gray-500" data-id="element-232">
                  Direct je eigen adviseur aan de lijn
                </p>
              </motion.a>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-233">
              <motion.a href="https://wa.me/31612345678" target="_blank" rel="noopener noreferrer" className="block bg-white p-6 rounded-2xl shadow-card border border-gray-100 text-center group" whileHover={{
              y: -4,
              boxShadow: '0 20px 40px rgba(0, 45, 90, 0.08)'
            }} transition={{
              duration: 0.2
            }} data-id="element-234">
                <div className="w-14 h-14 bg-[#25D366]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#25D366]/20 transition-colors" data-id="element-235">
                  <WhatsAppIcon className="w-7 h-7 text-[#25D366]" data-id="element-236" />
                </div>
                <h3 className="text-lg font-bold text-navy mb-1" data-id="element-237">WhatsApp</h3>
                <p className="text-xl font-bold text-[#25D366] mb-2" data-id="element-238">
                  06-12345678
                </p>
                <p className="text-sm text-gray-500" data-id="element-239">
                  Snel een vraag? App me gerust
                </p>
              </motion.a>
            </FadeIn>

            <FadeIn delay={0.3} data-id="element-240">
              <motion.a href="mailto:info@jdg-advies.nl" className="block bg-white p-6 rounded-2xl shadow-card border border-gray-100 text-center group" whileHover={{
              y: -4,
              boxShadow: '0 20px 40px rgba(0, 45, 90, 0.08)'
            }} transition={{
              duration: 0.2
            }} data-id="element-241">
                <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gold/10 transition-colors" data-id="element-242">
                  <MailIcon className="w-7 h-7 text-gold" data-id="element-243" />
                </div>
                <h3 className="text-lg font-bold text-navy mb-1" data-id="element-244">E-mail</h3>
                <p className="text-xl font-bold text-gold mb-2 break-all" data-id="element-245">
                  info@jdg-advies.nl
                </p>
                <p className="text-sm text-gray-500" data-id="element-246">
                  Ik reageer binnen 24 uur
                </p>
              </motion.a>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Two Column: Form + Trust Panel */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-247">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-248">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12" data-id="element-249">
            {/* Left: Forms (3 cols) */}
            <FadeIn className="lg:col-span-3 space-y-8" data-id="element-250">
              {/* Bel me terug - Quick Action */}
              <div className="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-251">
                <h3 className="text-xl font-bold text-navy mb-1" data-id="element-252">
                  Liever dat ik jou bel?
                </h3>
                <p className="text-gray-500 text-sm mb-5" data-id="element-253">
                  Vul je naam en nummer in, dan bel ik je meestal binnen een
                  paar uur.
                </p>

                <AnimatePresence mode="wait" data-id="element-254">
                  {isCallbackSuccess ? <motion.div key="callback-success" initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0
                }} className="bg-green-50 text-green-800 p-6 rounded-xl border border-green-200 flex items-center gap-4" data-id="element-255">
                      <CheckCircleIcon className="w-8 h-8 text-green-600 flex-shrink-0" data-id="element-256" />
                      <div data-id="element-257">
                        <p className="font-bold" data-id="element-258">Terugbelverzoek ontvangen!</p>
                        <p className="text-green-700 text-sm" data-id="element-259">
                          Ik bel je zo snel mogelijk — meestal binnen een paar
                          uur.
                        </p>
                      </div>
                    </motion.div> : <motion.form key="callback-form" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} onSubmit={handleCallbackSubmit} className="flex flex-col sm:flex-row gap-3" data-id="element-260">
                      <div className="relative flex-1" data-id="element-261">
                        <input type="text" id="callback-name" name="name" required value={callbackForm.name} onChange={handleCallbackChange} className="block px-4 pb-2.5 pt-6 w-full text-navy bg-warm rounded-brand border border-gray-200 appearance-none focus:outline-none focus:ring-0 focus:border-gold peer transition-colors" placeholder=" " data-id="element-262" />
                        <label htmlFor="callback-name" className="absolute text-gray-500 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[0] left-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3 peer-focus:text-gold" data-id="element-263">
                          Naam *
                        </label>
                      </div>
                      <div className="relative flex-1" data-id="element-264">
                        <input type="tel" id="callback-phone" name="phone" required value={callbackForm.phone} onChange={handleCallbackChange} className="block px-4 pb-2.5 pt-6 w-full text-navy bg-warm rounded-brand border border-gray-200 appearance-none focus:outline-none focus:ring-0 focus:border-gold peer transition-colors" placeholder=" " data-id="element-265" />
                        <label htmlFor="callback-phone" className="absolute text-gray-500 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[0] left-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3 peer-focus:text-gold" data-id="element-266">
                          Telefoonnummer *
                        </label>
                      </div>
                      <Button type="submit" variant="primary" disabled={isCallbackSubmitting} className="sm:self-stretch" icon={isCallbackSubmitting ? <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-268">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-269" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-270" />
                            </svg> : <PhoneIcon className="w-4 h-4" data-id="element-271" />} iconPosition="left" data-id="element-267">
                        {isCallbackSubmitting ? 'Verzenden...' : 'Bel mij terug'}
                      </Button>
                    </motion.form>}
                </AnimatePresence>
              </div>

              {/* Main Contact Form */}
              <div className="bg-white p-8 md:p-10 rounded-2xl shadow-card border border-gray-100" data-id="element-272">
                <h2 className="text-2xl font-bold text-navy mb-2" data-id="element-273">
                  Stuur een bericht
                </h2>
                <p className="text-gray-500 mb-8" data-id="element-274">
                  Vul het formulier in en ik neem zo snel mogelijk contact op.
                </p>

                <AnimatePresence mode="wait" data-id="element-275">
                  {isSuccess ? <motion.div key="success" initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0
                }} className="bg-green-50 text-green-800 p-8 rounded-2xl border border-green-200 flex flex-col items-center text-center min-h-[400px] justify-center" data-id="element-276">
                      <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} transition={{
                    type: 'spring',
                    stiffness: 200,
                    delay: 0.1
                  }} className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6" data-id="element-277">
                        <CheckCircleIcon className="w-10 h-10 text-green-600" data-id="element-278" />
                      </motion.div>
                      <h3 className="text-2xl font-bold mb-3" data-id="element-279">
                        Bericht verzonden!
                      </h3>
                      <p className="text-green-700 max-w-sm mb-6" data-id="element-280">
                        Bedankt voor je bericht. Ik neem meestal binnen een paar
                        uur contact met je op — vaak nog dezelfde dag.
                      </p>
                      <div className="bg-green-100 rounded-xl p-4 max-w-sm w-full" data-id="element-281">
                        <p className="text-green-800 font-medium text-sm mb-3" data-id="element-282">
                          Liever niet wachten? Bel of app me direct:
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3" data-id="element-283">
                          <a href="tel:0232052161" className="flex items-center justify-center gap-2 bg-white text-navy font-bold py-2.5 px-4 rounded-brand text-sm border border-green-200 hover:border-navy transition-colors" data-id="element-284">
                            <PhoneIcon className="w-4 h-4" data-id="element-285" />
                            023-205 2161
                          </a>
                          <a href="https://wa.me/31612345678" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold py-2.5 px-4 rounded-brand text-sm hover:bg-[#1da851] transition-colors" data-id="element-286">
                            <WhatsAppIcon className="w-4 h-4" data-id="element-287" />
                            WhatsApp
                          </a>
                        </div>
                      </div>
                    </motion.div> : <motion.form key="form" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} onSubmit={handleSubmit} className="space-y-5" data-id="element-288">
                      <div className="relative" data-id="element-289">
                        <input type="text" id="name" name="name" required value={formState.name} onChange={handleChange} className="block px-4 pb-2.5 pt-6 w-full text-navy bg-warm rounded-brand border border-gray-200 appearance-none focus:outline-none focus:ring-0 focus:border-gold peer transition-colors" placeholder=" " data-id="element-290" />
                        <label htmlFor="name" className="absolute text-gray-500 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[0] left-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3 peer-focus:text-gold" data-id="element-291">
                          Naam *
                        </label>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-292">
                        <div className="relative" data-id="element-293">
                          <input type="email" id="email" name="email" required value={formState.email} onChange={handleChange} className="block px-4 pb-2.5 pt-6 w-full text-navy bg-warm rounded-brand border border-gray-200 appearance-none focus:outline-none focus:ring-0 focus:border-gold peer transition-colors" placeholder=" " data-id="element-294" />
                          <label htmlFor="email" className="absolute text-gray-500 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[0] left-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3 peer-focus:text-gold" data-id="element-295">
                            E-mailadres *
                          </label>
                        </div>

                        <div className="relative" data-id="element-296">
                          <input type="tel" id="phone" name="phone" value={formState.phone} onChange={handleChange} className="block px-4 pb-2.5 pt-6 w-full text-navy bg-warm rounded-brand border border-gray-200 appearance-none focus:outline-none focus:ring-0 focus:border-gold peer transition-colors" placeholder=" " data-id="element-297" />
                          <label htmlFor="phone" className="absolute text-gray-500 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[0] left-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3 peer-focus:text-gold" data-id="element-298">
                            Telefoonnummer
                          </label>
                        </div>
                      </div>

                      <div className="relative" data-id="element-299">
                        <select id="subject" name="subject" required value={formState.subject} onChange={handleChange} className="block px-4 pb-2.5 pt-6 w-full text-navy bg-warm rounded-brand border border-gray-200 appearance-none focus:outline-none focus:ring-0 focus:border-gold peer transition-colors" data-id="element-300">
                          <option value="" disabled hidden data-id="element-301"></option>
                          <option value="terugbelverzoek" data-id="element-302">Bel me terug</option>
                          <option value="hypotheek" data-id="element-303">Hypotheek</option>
                          <option value="woonboot" data-id="element-304">Woonboot</option>
                          <option value="verzekering" data-id="element-305">Verzekering</option>
                          <option value="schade" data-id="element-306">Schade melden</option>
                          <option value="anders" data-id="element-307">Anders</option>
                        </select>
                        <label htmlFor="subject" className="absolute text-gray-500 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[0] left-4 peer-focus:text-gold" data-id="element-308">
                          Waar kan ik je mee helpen? *
                        </label>
                      </div>

                      <div className="relative" data-id="element-309">
                        <textarea id="message" name="message" required rows={4} value={formState.message} onChange={handleChange} className="block px-4 pb-2.5 pt-6 w-full text-navy bg-warm rounded-brand border border-gray-200 appearance-none focus:outline-none focus:ring-0 focus:border-gold peer resize-none transition-colors" placeholder=" " data-id="element-310" />
                        <label htmlFor="message" className="absolute text-gray-500 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[0] left-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3 peer-focus:text-gold" data-id="element-311">
                          Bericht *
                        </label>
                      </div>

                      <div className="flex items-start" data-id="element-312">
                        <div className="flex items-center h-5 mt-0.5" data-id="element-313">
                          <input id="privacyConsent" name="privacyConsent" type="checkbox" required checked={formState.privacyConsent} onChange={handleChange} className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-gold text-gold" data-id="element-314" />
                        </div>
                        <label htmlFor="privacyConsent" className="ml-3 text-sm text-gray-600 leading-snug" data-id="element-315">
                          Ik ga akkoord met de verwerking van mijn gegevens
                          conform de{' '}
                          <a href="#" className="text-gold hover:underline" data-id="element-316">
                            privacyverklaring
                          </a>
                          .
                        </label>
                      </div>

                      <Button type="submit" variant="primary" size="lg" disabled={isSubmitting || !formState.privacyConsent} className="w-full" icon={isSubmitting ? <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-318">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-319" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-320" />
                            </svg> : <SendIcon className="w-5 h-5" data-id="element-321" />} iconPosition="left" data-id="element-317">
                        {isSubmitting ? 'Verzenden...' : 'Stuur je vraag'}
                      </Button>
                    </motion.form>}
                </AnimatePresence>
              </div>
            </FadeIn>

            {/* Right: Trust Panel (2 cols) */}
            <FadeIn delay={0.2} className="lg:col-span-2 space-y-6" data-id="element-322">
              {/* Jordy Photo + Expectations */}
              <div className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-323">
                <div className="flex items-center gap-5 mb-6" data-id="element-324">
                  <img src="https://cdn.magicpatterns.com/uploads/8YqgEQJ5jSY63NDsDuuJzL/5A2A7661-bewerkt-3.jpg" alt="Jordy de Gier" className="w-20 h-20 rounded-full object-cover object-top shadow-md border-2 border-gold/30" data-id="element-325" />
                  <div data-id="element-326">
                    <h3 className="text-lg font-bold text-navy" data-id="element-327">
                      Jordy de Gier
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-328">
                      Erkend Financieel Adviseur
                    </p>
                  </div>
                </div>

                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-329">
                  Wat kun je verwachten?
                </h4>
                <ul className="space-y-4" data-id="element-330">
                  <li className="flex items-start gap-3" data-id="element-331">
                    <CheckCircleIcon className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" data-id="element-332" />
                    <div data-id="element-333">
                      <p className="font-bold text-navy text-sm" data-id="element-334">
                        Gratis kennismaking van ~30 min
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-335">
                        Vrijblijvend en zonder verplichtingen
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-336">
                    <CheckCircleIcon className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" data-id="element-337" />
                    <div data-id="element-338">
                      <p className="font-bold text-navy text-sm" data-id="element-339">
                        Persoonlijk advies, geen verkooppraatje
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-340">
                        Eerlijk en onafhankelijk
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-341">
                    <CheckCircleIcon className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" data-id="element-342" />
                    <div data-id="element-343">
                      <p className="font-bold text-navy text-sm" data-id="element-344">
                        Binnen 24 uur een reactie
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-345">
                        Vaak al dezelfde dag
                      </p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Erkend Adviseur Badge */}
              <div className="bg-white p-6 rounded-2xl shadow-card border border-gray-100 flex items-center gap-4" data-id="element-346">
                <img src="https://cdn.magicpatterns.com/uploads/hLrKue75gCT8qjkamHbfdK/JDG_Erkend_adviseur.png" alt="Erkend Financieel Adviseur" className="h-14 object-contain" data-id="element-347" />
                <div data-id="element-348">
                  <p className="font-bold text-navy text-sm" data-id="element-349">
                    Erkend Financieel Adviseur
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-350">
                    AFM-geregistreerd · Kifid aangesloten
                  </p>
                </div>
              </div>

              {/* Opening Hours */}
              <div className="bg-white p-6 rounded-2xl shadow-card border border-gray-100" data-id="element-351">
                <div className="flex items-center gap-3 mb-4" data-id="element-352">
                  <div className="w-10 h-10 bg-navy/5 rounded-full flex items-center justify-center" data-id="element-353">
                    <ClockIcon className="w-5 h-5 text-gold" data-id="element-354" />
                  </div>
                  <h4 className="font-bold text-navy" data-id="element-355">Openingstijden</h4>
                </div>
                <div className="space-y-2 text-sm" data-id="element-356">
                  <div className="flex justify-between" data-id="element-357">
                    <span className="text-gray-600" data-id="element-358">Maandag – Vrijdag</span>
                    <span className="font-bold text-navy" data-id="element-359">09:00 – 17:30</span>
                  </div>
                  <div className="flex justify-between" data-id="element-360">
                    <span className="text-gray-600" data-id="element-361">Zaterdag</span>
                    <span className="font-bold text-navy" data-id="element-362">Op afspraak</span>
                  </div>
                  <div className="flex justify-between" data-id="element-363">
                    <span className="text-gray-600" data-id="element-364">Zondag</span>
                    <span className="font-bold text-navy" data-id="element-365">Gesloten</span>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-3" data-id="element-366">
                  Buiten openingstijden? Stuur een WhatsApp, ik reageer zo snel
                  mogelijk.
                </p>
              </div>

              {/* Google Maps Card */}
              <a href="https://maps.google.com/?q=Sloterweg+228+Badhoevedorp" target="_blank" rel="noopener noreferrer" className="block" data-id="element-367">
                <motion.div className="bg-navy p-6 rounded-2xl text-white relative overflow-hidden group" whileHover={{
                scale: 1.01
              }} transition={{
                duration: 0.2
              }} data-id="element-368">
                  <div className="absolute inset-0 opacity-5" style={{
                  backgroundImage: 'radial-gradient(#fff 1.5px, transparent 1.5px)',
                  backgroundSize: '24px 24px'
                }} data-id="element-369" />
                  <div className="relative z-10 flex items-start gap-4" data-id="element-370">
                    <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-371">
                      <MapPinIcon className="w-6 h-6 text-gold" data-id="element-372" />
                    </div>
                    <div className="flex-1" data-id="element-373">
                      <p className="font-bold text-lg mb-1" data-id="element-374">Sloterweg 228</p>
                      <p className="text-gray-300 text-sm mb-3" data-id="element-375">
                        1171 CX Badhoevedorp
                      </p>
                      <span className="inline-flex items-center gap-1.5 text-gold text-sm font-bold group-hover:underline" data-id="element-376">
                        Open in Google Maps
                        <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-377" />
                      </span>
                    </div>
                  </div>
                </motion.div>
              </a>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Mini Trust Bar */}
      <section className="py-8 bg-white border-t border-gray-100" data-id="element-378">
        <div className="max-w-5xl mx-auto px-4 md:px-8" data-id="element-379">
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8" data-id="element-380">
            <StatBadge icon={<ShieldCheckIcon className="w-4 h-4 text-gold" data-id="element-382" />} text="Erkend Adviseur" data-id="element-381" />
            <StatBadge icon={<ShieldCheckIcon className="w-4 h-4 text-gold" data-id="element-384" />} text="AFM-geregistreerd" data-id="element-383" />
            <StatBadge icon={<StarIcon className="w-4 h-4 fill-gold text-gold" data-id="element-386" />} text="9.8/10 Advieskeuze" data-id="element-385" />
          </div>
        </div>
      </section>
    </div>;
}